// Shared logic for matching logged catches against the club's Catch & Release
// records (senior xlsx book + junior records list). Written as CommonJS so it
// can be required both from the browser bundle (via ES import interop) and
// from the standalone Node script in scripts/check-records.js.

// Species labels in the record sources don't always match the canonical
// species names used in cfsa-data.js / the catches table (different casing,
// abbreviations, or historic names). This table maps the normalized record
// label to the canonical boat/shore species name it refers to; species with
// no CFSA equivalent (and therefore no way to ever be logged) are left out.
const RECORD_SPECIES_ALIASES = {
  breamcouches: { boat: "Bream (Couch's)", shore: "Bream (Couch's)" },
  // Boat and shore book-keep this one under different canonical names (with
  // different weight thresholds), but it's a single row in the record book —
  // `display` lets buildDisplayRecords put both halves on the same row.
  dogfishlesser: { boat: "L.S.D.", shore: "Lesser Spotted Dogfish", display: "Lesser Spotted Dogfish" },
  // Same species as dogfishlesser above — the record book has used both the
  // abbreviated and the fully spelled-out label across different years.
  lesserspotteddogfish: { boat: "L.S.D.", shore: "Lesser Spotted Dogfish", display: "Lesser Spotted Dogfish" },
  eelsilver: { boat: null, shore: "Eel (Silver)" },
  forkbeardgrt: { boat: "Forkbeard (Greater)", shore: null },
  forkbeardlsr: { boat: null, shore: "Forkbeard (Lesser)" },
  herring: { boat: "Herring", shore: "Herring" },
  mackerelbonito: { boat: null, shore: "Mackerel (Bonito)" },
  mulletgoldengr: { boat: "Mullet (G. Grey)", shore: "Mullet (G. Grey)" },
  mulletred: { boat: null, shore: "Mullet (Striped Red)" },
  starrysmoothhound: { boat: "Smooth Hound (St)", shore: "Smooth Hound (St)" },
  sunfish: { boat: null, shore: "Sunfish" },
  solecommon: { boat: "Sole (Dover)", shore: "Sole (Dover)" },
  tope: { boat: "Tope (C&R Only)", shore: "Tope (C&R Only)" },
  wrassecorkwing: { boat: null, shore: "Wrasse (Corkwing)" },
};

function normalizeSpeciesKey(str) {
  return String(str || "").toLowerCase().replace(/[^a-z0-9]/g, "");
}

function resolveCanonicalSpecies(rawLabel, location, boatSpeciesNames, shoreSpeciesNames) {
  const key = normalizeSpeciesKey(rawLabel);
  if (!key) return null;
  const list = location === "boat" ? boatSpeciesNames : shoreSpeciesNames;
  const direct = list.find((name) => normalizeSpeciesKey(name) === key);
  if (direct) return direct;
  const alias = RECORD_SPECIES_ALIASES[key];
  return alias ? alias[location] : null;
}

// Boat/shore canonical species names can differ for what is really one
// species (see the `display` override on dogfishlesser above). This maps a
// per-location canonical name back to the shared label its record row should
// be grouped/displayed under, so a species split across two canonical names
// still renders as a single boat+shore row.
const DISPLAY_NAME_BY_CANONICAL = {};
Object.values(RECORD_SPECIES_ALIASES).forEach((alias) => {
  if (!alias.display) return;
  if (alias.boat) DISPLAY_NAME_BY_CANONICAL[alias.boat] = alias.display;
  if (alias.shore) DISPLAY_NAME_BY_CANONICAL[alias.shore] = alias.display;
});

function getDisplaySpecies(canonicalSpecies) {
  return DISPLAY_NAME_BY_CANONICAL[canonicalSpecies] || canonicalSpecies;
}

const KG_PER_OZ = 0.0283495231;

// Record weights are entered as "LBS.OZS.DRMS" (16 oz/lb, 16 drms/oz), but
// real-world entries are inconsistent: dot or space separated, extra spaces,
// stray leading dots, double-digit drams etc. Pull out up to 3 numbers.
function parseLbsOzDrmsToKg(raw) {
  const str = String(raw ?? "").trim();
  if (!str) return null;
  const nums = str
    .split(/[.\s]+/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => parseInt(p, 10))
    .filter((n) => !isNaN(n));
  if (nums.length === 0) return null;
  const [lbs = 0, oz = 0, dr = 0] = nums;
  const totalOz = lbs * 16 + oz + dr / 16;
  const kg = totalOz * KG_PER_OZ;
  if (!kg) return null;
  return { lbs, oz, dr, kg };
}

function kgToLbsOzDrmsStr(kg) {
  const totalOz = kg / KG_PER_OZ;
  const totalDr = Math.round(totalOz * 16);
  const lbs = Math.floor(totalDr / 256);
  const oz = Math.floor((totalDr % 256) / 16);
  const dr = totalDr % 16;
  return `${lbs}.${oz}.${dr}`;
}

// True if the catch is no heavier than the record, compared in whole drams.
// Catches and records are converted to kg with slightly different constants,
// so a raw kg comparison would make an equal-weight catch look heavier.
function isNotHeavier(catchKg, recordKg) {
  const toDrms = (kg) => Math.round((kg / KG_PER_OZ) * 16);
  return toDrms(catchKg) <= toDrms(recordKg);
}

function formatKgAsLbsOz(kg) {
  const totalDr = Math.round((kg / KG_PER_OZ) * 16); // round to nearest dram
  const lbs = Math.floor(totalDr / 256);
  const oz = Math.floor((totalDr % 256) / 16);
  const dr = totalDr % 16;
  return `${lbs} lbs ${oz} oz ${dr} drms`;
}

// entries: [{ speciesLabel, boatYear, boatCaptor, boatWeight, shoreYear, shoreCaptor, shoreWeight, ref }]
// Builds a Map keyed "<canonical species>::<location>" -> record details.
// The record book should only ever have one row per species/location, but if
// a data-entry error produces more than one, keep only the heaviest fish
// (ties keep whichever is seen last — either is a valid single entry).
function buildRecordsIndex(entries, boatSpeciesNames, shoreSpeciesNames) {
  const index = new Map();
  entries.forEach((entry) => {
    ["boat", "shore"].forEach((location) => {
      const canonical = resolveCanonicalSpecies(entry.speciesLabel, location, boatSpeciesNames, shoreSpeciesNames);
      if (!canonical) return;
      const weightRaw = location === "boat" ? entry.boatWeight : entry.shoreWeight;
      const year = location === "boat" ? entry.boatYear : entry.shoreYear;
      const captor = location === "boat" ? entry.boatCaptor : entry.shoreCaptor;
      const hasRecordData = String(weightRaw || "").trim() !== "" || String(captor || "").trim() !== "";
      if (!hasRecordData) return;
      const parsed = parseLbsOzDrmsToKg(weightRaw);
      const key = `${canonical}::${location}`;
      const candidate = {
        species: canonical,
        location,
        year: year != null ? String(year) : "",
        captor: captor != null ? String(captor) : "",
        weightRaw,
        kg: parsed ? parsed.kg : null,
        ref: entry.ref,
        sheet: entry.sheet,
      };
      const existing = index.get(key);
      if (!existing || existing.kg == null || (candidate.kg != null && candidate.kg >= existing.kg)) {
        index.set(key, candidate);
      }
    });
  });
  return index;
}

// catches: [{ species, location, weight_kg, angler_name, weigh_date, ... }]
// boatSpeciesNames/shoreSpeciesNames make a species/location combo with no
// existing row eligible as a first-ever record, rather than being silently
// skipped as "untracked" (a real CFSA category can simply have never been
// caught before). previous.ref is null in that case — there's no row to
// update, so the caller must insert a new one instead.
// Returns the single heaviest beaten-record catch per species/location key.
function findNewRecords(catches, recordsIndex, boatSpeciesNames = [], shoreSpeciesNames = []) {
  const bestByKey = new Map();
  catches.forEach((c) => {
    if (!c.species || !c.location || c.weight_kg == null) return;
    const key = `${c.species}::${c.location}`;
    let existing = recordsIndex.get(key);
    if (!existing) {
      const validNames = c.location === "boat" ? boatSpeciesNames : shoreSpeciesNames;
      if (!validNames.includes(c.species)) return; // not a real CFSA category for this location
      existing = { species: c.species, location: c.location, kg: null, year: "", captor: "", ref: null };
    }
    if (existing.kg != null && isNotHeavier(c.weight_kg, existing.kg)) return;
    const current = bestByKey.get(key);
    if (!current || c.weight_kg > current.catch.weight_kg) {
      bestByKey.set(key, { catch: c, previous: existing });
    }
  });
  return Array.from(bestByKey.values());
}

module.exports = {
  RECORD_SPECIES_ALIASES,
  normalizeSpeciesKey,
  resolveCanonicalSpecies,
  getDisplaySpecies,
  parseLbsOzDrmsToKg,
  kgToLbsOzDrmsStr,
  formatKgAsLbsOz,
  isNotHeavier,
  buildRecordsIndex,
  findNewRecords,
  KG_PER_OZ,
};
